import { useCallback, useEffect, useState } from "react";
import "../../styles/amazon.css";
import { fetchAmazonOverview, postAmazonSync } from "../../api/client";
import type { AmzOverviewResponse } from "../../types/amazon";
import { StateBanner } from "../../components/StateBanner";
import { DashboardView } from "./DashboardView";
import { HistoryView } from "./HistoryView";
import { LibraryView } from "./LibraryView";
import { ProductDetail } from "./ProductDetail";
import { ProductsView } from "./ProductsView";
import { errorMessage } from "./helpers";
import type { AmazonView, DetailTab, ProductFilter } from "./helpers";
import { useAmzToast } from "./useAmzToast";

interface NavDef {
  label: string;
  view?: Exclude<AmazonView, "detail">;
  off?: boolean;
}

const NAV: NavDef[] = [
  { label: "ダッシュボード", view: "dashboard" },
  { label: "商品", view: "products" },
  { label: "A+コンテンツ", off: true },
  { label: "画像ライブラリ", view: "library" },
  { label: "AIレビュー", off: true },
  { label: "更新履歴", view: "history" },
  { label: "設定", off: true },
];

export function AmazonPage() {
  const [view, setView] = useState<AmazonView>("dashboard");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<ProductFilter>("all");
  const [sku, setSku] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>("overview");
  const [overview, setOverview] = useState<AmzOverviewResponse | null>(null);
  const [ovError, setOvError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [names, setNames] = useState<Record<string, string | null>>({});
  const { toastMessage, showToast } = useAmzToast();

  const loadOverview = useCallback(async () => {
    try {
      const r = await fetchAmazonOverview();
      setOverview(r);
      setOvError(null);
    } catch (e) {
      setOvError(errorMessage(e));
    }
  }, []);

  // 一覧系の画面に来るたびに overview を取り直す（詳細での保存・反映の結果を反映）
  useEffect(() => {
    if (view !== "detail" && view !== "library") void loadOverview();
  }, [view, loadOverview]);

  const openProduct = useCallback((s: string, t: DetailTab) => {
    setSku(s);
    setTab(t);
    setView("detail");
  }, []);

  const goFilter = (f: ProductFilter) => {
    setFilter(f);
    setView("products");
  };

  const syncAll = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await postAmazonSync();
      setOverview(r);
      showToast("Amazonから最新の商品情報を取得しました。未反映の下書きは上書きしていません。");
    } catch (e) {
      showToast(`取得に失敗しました: ${errorMessage(e)}`);
    } finally {
      setSyncing(false);
    }
  };

  const onProductName = useCallback((s: string, name: string | null) => {
    setNames((m) => (m[s] === name ? m : { ...m, [s]: name }));
  }, []);

  const onSearch = (value: string) => {
    setQ(value);
    if (view === "dashboard" || view === "detail") setView("products");
  };

  const activeNav = view === "detail" ? "products" : view;
  const errCount = overview?.stats.errors ?? 0;

  const detailName = sku ? (names[sku] ?? overview?.products.find((p) => p.sku === sku)?.itemName ?? sku) : "";
  const crumbs: { label: string; onClick?: () => void }[] =
    view === "dashboard"
      ? [{ label: "ダッシュボード" }]
      : view === "products"
        ? [{ label: "商品" }]
        : view === "history"
          ? [{ label: "更新履歴" }]
          : view === "library"
            ? [{ label: "画像ライブラリ" }]
            : [{ label: "商品", onClick: () => setView("products") }, { label: detailName }];

  let body: JSX.Element;
  if (view === "library") {
    // 画像ライブラリは overview が無くても開ける（公開ファイルを直接読む）
    body = (
      <main className="amz-main">
        <LibraryView overview={overview} onOpen={openProduct} showToast={showToast} />
      </main>
    );
  } else if (view === "detail" && sku) {
    body = (
      <ProductDetail
        key={sku}
        sku={sku}
        tab={tab}
        onTab={setTab}
        showToast={showToast}
        onOverviewChanged={() => void loadOverview()}
        onProductName={onProductName}
      />
    );
  } else if (!overview) {
    body = (
      <main className="amz-main">
        <div className="amz-pad">
          {ovError ? (
            <StateBanner kind="error" message={ovError} onRetry={() => void loadOverview()} />
          ) : (
            <StateBanner kind="loading" message="Amazon商品の一覧を取得しています。" />
          )}
        </div>
      </main>
    );
  } else if (view === "dashboard") {
    body = (
      <main className="amz-main">
        <DashboardView overview={overview} onFilter={goFilter} onOpen={openProduct} onHistory={() => setView("history")} />
      </main>
    );
  } else if (view === "products") {
    body = (
      <main className="amz-main">
        <ProductsView
          overview={overview}
          q={q}
          filter={filter}
          onFilter={setFilter}
          onOpen={openProduct}
          onSyncAll={() => void syncAll()}
          syncing={syncing}
          onImported={(s) => {
            void loadOverview();
            openProduct(s, "page");
          }}
          showToast={showToast}
        />
      </main>
    );
  } else {
    body = (
      <main className="amz-main">
        <HistoryView />
      </main>
    );
  }

  return (
    <div className="amz-root">
      <nav className="amz-nav" aria-label="Amazon商品管理ナビゲーション">
        <div className="amz-brand">
          <div className="amz-logo">A</div>
          <div className="amz-brand-text">
            <b>Amazon</b>
            <span>Product Content Manager</span>
          </div>
        </div>
        {NAV.map((n) => (
          <button
            key={n.label}
            type="button"
            className={`amz-nav-item ${n.off ? "off" : ""} ${!n.off && activeNav === n.view ? "active" : ""}`}
            title={n.off ? "準備中" : undefined}
            aria-disabled={n.off ? true : undefined}
            onClick={() => {
              if (n.off || !n.view) return;
              if (n.view === "products") setFilter("all");
              setView(n.view);
            }}
          >
            <span>{n.label}</span>
            {n.view === "products" && errCount > 0 && <span className="amz-count">{errCount}</span>}
          </button>
        ))}
        <div className="amz-nav-spacer" />
        <div className="amz-nav-foot">
          <div className="amz-conn">
            <span className="amz-dot" />
            Amazon.co.jp 接続中
          </div>
          <div>村瀬 · 管理者</div>
        </div>
      </nav>

      <div className="amz-col">
        <header className="amz-top">
          <div className="amz-crumbs">
            {crumbs.map((c, i) => (
              <span key={`${c.label}-${i}`} style={{ display: "contents" }}>
                {i > 0 && <span className="amz-crumb-sep">/</span>}
                <button
                  type="button"
                  className={`amz-crumb ${c.onClick ? "link" : ""} ${i === crumbs.length - 1 ? "last" : ""}`}
                  onClick={c.onClick}
                >
                  {c.label}
                </button>
              </span>
            ))}
          </div>
          <div className="amz-top-right">
            <input
              className="amz-search"
              value={q}
              placeholder="商品名・SKU・ASINで検索"
              aria-label="商品を検索"
              onChange={(e) => onSearch(e.target.value)}
            />
            <span className="amz-mkt">Amazon.co.jp · JPY</span>
          </div>
        </header>
        <div className="amz-body">{body}</div>
      </div>

      {toastMessage && (
        <div className="amz-toast" role="status">
          {toastMessage}
        </div>
      )}
    </div>
  );
}
